import axios from "../api/axios"
import { CreditNoteType } from "../static/types/creditNoteType"

// Interface pour la création d'un avoir
export interface CreateAvoirData {
  annonce_id: number
  montant_avoir: number
  statut: "pending" | "utilisé" | "à_rembourser" | "remboursé"
  rib?: string
}

// Interface pour la mise à jour d'un avoir
export interface UpdateAvoirData {
  montant_avoir?: number
  statut?: "pending" | "utilisé" | "à_rembourser" | "remboursé"
  rib?: string
}

// Interface pour les données utilisateur agrégées
export interface UserAvoirData {
  userId: number
  name: string
  email: string
  soldePending: number
  montantARembourser: number
  montantRembourse: number
  countPending: number
  countARembourser: number
  countRembourse: number
  avoirsDetails: {
    pending: CreditNoteType[]
    aRembourser: CreditNoteType[]
    rembourse: CreditNoteType[]
  }
}

// Interface pour la réponse de getAllAvoirs
interface GetAllAvoirsResponse {
  success: boolean
  avoirs: CreditNoteType[]
  pagination?: {
    currentPage: number
    totalPages: number
    totalItems: number
    itemsPerPage: number
  }
  data?: CreditNoteType[]
}

// Interface pour les statistiques
interface AvoirStats {
  total: number
  pending: number
  utilise: number
  a_rembourser: number
  rembourse: number
  montant_total: number
}

// Interface pour la réponse de remboursement
interface RemboursementResponse {
  success: boolean
  montantRembourse: number
  message?: string
}

// Interface pour la validation du remboursement
interface CanRequestRemboursement {
  canRequest: boolean
  soldeDisponible: number
  message?: string
}

/**
 * Récupérer tous les avoirs
 */
export const getAllAvoirs = async (): Promise<CreditNoteType[]> => {
  try {
    const response = await axios.get<GetAllAvoirsResponse>("/avoirs")
    return response.data.avoirs || response.data.data || []
  } catch (error: any) {
    console.error("Erreur lors de la récupération des avoirs:", error)
    throw new Error(error.response?.data?.message || "Erreur lors de la récupération des avoirs")
  }
}

/**
 * Récupérer tous les avoirs avec détails complets
 */
export const getAvoirs = async (): Promise<CreditNoteType[]> => {
  try {
    const response = await axios.get<{ data: CreditNoteType[], message?: string }>("/avoirs/get-avoirs")
    return response.data.data || []
  } catch (error: any) {
    console.error("Erreur lors de la récupération des avoirs:", error)
    throw new Error(error.response?.data?.message || "Erreur lors de la récupération des avoirs")
  }
}

/**
 * Récupérer les avoirs utilisables pour un utilisateur (statut "pending")
 * VERSION CORRIGÉE - Retourne un tableau vide si userId invalide
 */
export const getUtilisableAvoirs = async (user_Id: string): Promise<CreditNoteType[]> => {
  try {
    if (!user_Id || user_Id === 'undefined' || user_Id === 'null' || user_Id.trim() === '') {
      console.warn('[AVOIR_SERVICE] getUtilisableAvoirs - userId invalide:', user_Id)
      return []
    }

    const response = await axios.get<{ data: CreditNoteType[], message?: string }>(`/avoirs/utilisables/${user_Id}`)
    return response.data.data || []
  } catch (error: any) {
    console.error('[AVOIR_SERVICE] Erreur getUtilisableAvoirs:', error)
    if (error.response?.status === 400 || error.response?.status === 404) {
      return []
    }
    return []
  }
}

/**
 * Récupérer un avoir par ID d'annonce
 */
export const getAvoirByAnnonce = async (annonceId: number): Promise<CreditNoteType | null> => {
  try {
    const response = await axios.get<{ avoir?: CreditNoteType, data?: CreditNoteType }>(`/avoirs/annonce/${annonceId}`)
    return response.data.avoir || response.data.data || null
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null
    }
    console.error('[AVOIR_SERVICE] Erreur getAvoirByAnnonce:', error)
    throw new Error(error.response?.data?.message || "Erreur lors de la récupération de l'avoir")
  }
}

/**
 * Créer un nouvel avoir (toujours attaché à une annonce)
 */
export const createAvoir = async (avoirData: CreateAvoirData): Promise<CreditNoteType> => {
  try {
    console.log('[AVOIR_SERVICE] Création avoir:', avoirData)
    const response = await axios.post<{ avoir?: CreditNoteType, data?: CreditNoteType, success?: boolean }>("/avoirs", avoirData)
    const avoir = response.data.avoir || response.data.data
    if (!avoir) {
      throw new Error("Réponse invalide du serveur")
    }
    return avoir
  } catch (error: any) {
    console.error('[AVOIR_SERVICE] Erreur createAvoir:', error)
    throw new Error(error.response?.data?.error || error.response?.data?.message || "Erreur lors de la création de l'avoir")
  }
}

/**
 * Mettre à jour un avoir existant
 */
export const updateAvoir = async (avoirId: number, updateData: UpdateAvoirData): Promise<CreditNoteType> => {
  try {
    const response = await axios.put<{ avoir?: CreditNoteType, data?: CreditNoteType }>(`/avoirs/${avoirId}`, updateData)
    const avoir = response.data.avoir || response.data.data
    if (!avoir) {
      throw new Error("Réponse invalide du serveur")
    }
    return avoir
  } catch (error: any) {
    console.error('[AVOIR_SERVICE] Erreur updateAvoir:', error)
    throw new Error(error.response?.data?.message || "Erreur lors de la mise à jour de l'avoir")
  }
}

/**
 * Mettre à jour le statut de plusieurs avoirs
 */
export const updateAvoirsStatus = async (
  idArray: number[], 
  statut: "pending" | "utilisé" | "à_rembourser" | "remboursé"
): Promise<void> => {
  try {
    await axios.put("/avoirs/update-status", {
      idArray: idArray.map(id => ({ id })),
      status: statut
    })
  } catch (error: any) {
    console.error('[AVOIR_SERVICE] Erreur updateAvoirsStatus:', error)
    throw new Error(error.response?.data?.message || "Erreur lors de la mise à jour du statut des avoirs")
  }
}

/**
 * Supprimer un avoir
 */
export const deleteAvoir = async (avoirId: number): Promise<void> => {
  try {
    await axios.delete(`/avoirs/${avoirId}`)
  } catch (error: any) {
    console.error('[AVOIR_SERVICE] Erreur deleteAvoir:', error)
    throw new Error(error.response?.data?.message || "Erreur lors de la suppression de l'avoir")
  }
}

/**
 * Récupérer les statistiques des avoirs
 */
export const getAvoirStats = async (): Promise<AvoirStats> => {
  try {
    const response = await axios.get<{ stats?: AvoirStats, data?: AvoirStats }>("/avoirs/stats")
    const stats = response.data.stats || response.data.data
    if (!stats) {
      throw new Error("Réponse invalide du serveur")
    }
    return stats
  } catch (error: any) {
    console.error('[AVOIR_SERVICE] Erreur getAvoirStats:', error)
    throw new Error(error.response?.data?.message || "Erreur lors de la récupération des statistiques")
  }
}

/**
 * Ouvrir la facture de remboursement dans un nouvel onglet
 */
export const openFactureInNewTab = (avoirId: number): void => {
  try {
    const baseURL = axios.defaults.baseURL || window.location.origin
    const url = `${baseURL.replace(/\/$/, "")}/avoirs/${avoirId}/facture`
    window.open(url, '_blank', 'noopener,noreferrer')
  } catch (error) {
    console.error('[AVOIR_SERVICE] Erreur openFactureInNewTab:', error)
    throw new Error("Impossible d'ouvrir la facture")
  }
}

/**
 * Télécharger la facture de remboursement
 */
export const downloadFacture = async (avoirId: number): Promise<void> => {
  try {
    const baseURL = axios.defaults.baseURL || window.location.origin
    const url = `${baseURL.replace(/\/$/, "")}/avoirs/${avoirId}/facture?download=true`
    const link = document.createElement('a')
    link.href = url
    link.download = `Remboursement-${avoirId}.pdf`
    link.target = '_blank'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  } catch (error: any) {
    console.error('[AVOIR_SERVICE] Erreur downloadFacture:', error)
    throw new Error("Impossible de télécharger la facture")
  }
}

/**
 * Récupérer le solde global disponible d'un utilisateur (statut pending uniquement)
 */
export const getSoldeAvoirUser = async (userId: string): Promise<number> => {
  try {
    if (!userId || userId === 'undefined' || userId === 'null' || userId.trim() === '') {
      console.warn('[AVOIR_SERVICE] getSoldeAvoirUser - userId invalide:', userId)
      return 0
    }

    const response = await axios.get<{ success?: boolean, solde: number }>(`/avoirs/solde/${userId}`)
    return response.data.solde || 0
  } catch (error: any) {
    console.error('[AVOIR_SERVICE] Erreur getSoldeAvoirUser:', error)
    return 0
  }
}

/**
 * Demander le remboursement d'un montant global d'avoirs
 * VERSION CLIENT - Marque les avoirs comme "à_rembourser"
 */
export const rembourserAvoirGlobal = async (
  userId: string, 
  montant: number, 
  rib: string
): Promise<RemboursementResponse> => {
  try {
    // Validations côté client
    if (!userId || userId === 'undefined' || userId === 'null' || userId.trim() === '') {
      throw new Error("ID utilisateur invalide")
    }
    
    if (montant <= 0) {
      throw new Error("Le montant doit être positif")
    }
    
    if (!rib || rib.trim().length === 0) {
      throw new Error("Le RIB est requis")
    }

    // Validation format RIB
    const cleanRib = rib.replace(/\s/g, '').toUpperCase()
    if (cleanRib.length !== 23 || !/^[A-Z0-9]{23}$/i.test(cleanRib)) {
      throw new Error("Format de RIB invalide (23 caractères alphanumériques requis)")
    }

    console.log('[AVOIR_SERVICE] Demande remboursement:', { userId, montant, rib: cleanRib })

    const response = await axios.post<RemboursementResponse>('/avoirs/rembourser-global', { 
      userId, 
      montant,
      rib: cleanRib
    })
    
    return {
      success: response.data.success || true,
      montantRembourse: response.data.montantRembourse || montant,
      message: response.data.message
    }
  } catch (error: any) {
    console.error('[AVOIR_SERVICE] Erreur rembourserAvoirGlobal:', error)
    throw new Error(
      error.response?.data?.error ||
      error.response?.data?.message || 
      error.message || 
      "Erreur lors de la demande de remboursement"
    )
  }
}

/**
 * Récupérer les avoirs détaillés d'un utilisateur groupés par statut
 */
export const getAvoirsDetailsUser = async (userId: string): Promise<UserAvoirData | null> => {
  try {
    if (!userId || userId === 'undefined' || userId === 'null' || userId.trim() === '') {
      console.warn('[AVOIR_SERVICE] getAvoirsDetailsUser - userId invalide:', userId)
      throw new Error("ID utilisateur invalide")
    }

    const response = await axios.get<{ data: CreditNoteType[] }>("/avoirs/get-avoirs")
    const avoirs = response.data.data || []
    
    const userAvoirs = avoirs.filter((avoir: CreditNoteType) => 
      avoir.annonce?.User?.id?.toString() === userId
    )
    
    if (userAvoirs.length === 0) {
      return null
    }

    const firstAvoir = userAvoirs[0]
    const userInfo = firstAvoir.annonce?.User

    const result: UserAvoirData = {
      userId: parseInt(userId),
      name: `${userInfo?.firstname || ''} ${userInfo?.name || ''}`.trim() || 'Client',
      email: userInfo?.email || '',
      soldePending: 0,
      montantARembourser: 0,
      montantRembourse: 0,
      countPending: 0,
      countARembourser: 0,
      countRembourse: 0,
      avoirsDetails: {
        pending: [],
        aRembourser: [],
        rembourse: []
      }
    }
    
    userAvoirs.forEach((avoir: CreditNoteType) => {
      const montant = Number(avoir.montant_avoir) || 0
      
      switch (avoir.statut) {
        case 'pending':
          result.soldePending += montant
          result.countPending++
          result.avoirsDetails.pending.push(avoir)
          break
        case 'à_rembourser':
          result.montantARembourser += montant
          result.countARembourser++
          result.avoirsDetails.aRembourser.push(avoir)
          break
        case 'remboursé':
          result.montantRembourse += montant
          result.countRembourse++
          result.avoirsDetails.rembourse.push(avoir)
          break
      }
    })
    
    return result
  } catch (error: any) {
    console.error('[AVOIR_SERVICE] Erreur getAvoirsDetailsUser:', error)
    throw new Error(
      error.response?.data?.message || 
      "Erreur lors de la récupération des détails des avoirs"
    )
  }
}

/**
 * Vérifier si un utilisateur peut demander le remboursement d'un montant
 */
export const canRequestRemboursement = async (
  userId: string, 
  montant: number
): Promise<CanRequestRemboursement> => {
  try {
    if (!userId || userId === 'undefined' || userId === 'null' || userId.trim() === '') {
      return {
        canRequest: false,
        soldeDisponible: 0,
        message: "ID utilisateur invalide"
      }
    }

    const solde = await getSoldeAvoirUser(userId)
    
    if (solde === 0) {
      return {
        canRequest: false,
        soldeDisponible: 0,
        message: "Aucun avoir disponible"
      }
    }
    
    if (montant > solde) {
      return {
        canRequest: false,
        soldeDisponible: solde,
        message: `Solde insuffisant (disponible: ${solde.toFixed(2)} €)`
      }
    }
    
    return {
      canRequest: true,
      soldeDisponible: solde,
      message: "Montant disponible"
    }
  } catch (error: any) {
    console.error('[AVOIR_SERVICE] Erreur canRequestRemboursement:', error)
    return {
      canRequest: false,
      soldeDisponible: 0,
      message: "Erreur lors de la vérification"
    }
  }
}

/**
 * Utilisation globale des avoirs (DEPRECATED - pour compatibilité)
 */
export const utiliserAvoirGlobal = async (
  userId: string,
  montant: number
): Promise<{ success: boolean, montantUtilise: number }> => {
  try {
    if (!userId || montant <= 0) {
      throw new Error("Paramètres invalides")
    }

    const response = await axios.post<{ success: boolean, montantUtilise: number }>('/avoirs/utiliser-global', {
      userId,
      montant
    })

    return response.data
  } catch (error: any) {
    console.error('[AVOIR_SERVICE] Erreur utiliserAvoirGlobal:', error)
    throw new Error(
      error.response?.data?.error ||
      error.response?.data?.message ||
      "Erreur lors de l'utilisation des avoirs"
    )
  }
}